import { ReactElement, ReactNode } from 'react';

import { echoLoader } from '@betterment/js-runtime';
import { ErrorElement } from '@betterment/shared-components';
import { type RouteObject } from 'react-router-dom';

import { NotFound } from './NotFound';

import ConfirmAdvisedSolo401kInvitation from '../../AcceptAdvisedSolo401kInvitation';
import { AccountPage } from '../../account/AccountPage';
import { AccountSecurityPage } from '../../account-security/AccountSecurityPage';
import { ActivityPage } from '../../activity/ActivityPage';
import AddAccount from '../../add-account/Controller';
import { Controller as AdvisorNetworkApp } from '../../advisor-network/Controller';
import { AgentsPlaygroundPage } from '../../agents-playground/AgentsPlaygroundPage';
import AgreementsApp from '../../apps/AgreementsApp';
import CheckingApp from '../../apps/CheckingApp';
import GenericErrorApp from '../../apps/GenericErrorApp';
import PerformanceRealtimeLoaderApp from '../../apps/PerformanceRealtimeLoaderApp';
import BuyOrderFlow from '../../buy-order-flow/Controller';
import DeferralSettingsApp from '../../DeferralSettingsApp/Controller';
import EditAddressPage from '../../edit-address-page/EditAddressPage';
import EditUserLevelSettings from '../../edit-user-level-settings/Controller';
import EmailVerificationCheckpointApp from '../../EmailVerificationCheckpoint';
import ExternallyAdvisedPlanFlow from '../../ExternallyAdvisedPlanFlow/Controller';
import { EchoRoute } from '../../features/echo/EchoRoute';
import FiveTwoNineOhioCDPurchaseApp from '../../FiveTwoNineOhioCDPurchaseApp';
import { ForecasterPage } from '../../forecaster/ForecasterPage';
import FourOhOneKSelectApp from '../../FourOhOneKAccountSelectApp';
import FourOhOneKDetailsApp from '../../FourOhOneKDetailsApp';
import Home from '../../home/home';
import IraDetailsApp from '../../IraDetailsApp';
import { AccountPerformancePage } from '../../performance/account/AccountPerformancePage';
import { UserPerformancePage } from '../../performance/user/UserPerformancePage';
import { PurposePage } from '../../purpose/PurposePage';
import ReferAFriendPage from '../../ReferAFriend/components/ReferAFriendPage';
import SellOrderFlow from '../../sell-order-flow/Controller';
import FullPageLoader from '../../Shared/components/FullPageLoader';
import { TestTrackFeatureGate } from '../../Shared/components-v2/test-track/TestTrackFeatureGate';
import { urlBuilder } from '../../Shared/urlBuilder';
import echoRoutes from '../echo-routes.json';
import { loadInitialProps } from '../loadInitialProps';

type PangeaRouteObject = RouteObject & {
  feature: string; // this attribute is necessary for statsd reporting: https://github.com/Betterment/gems/commit/d344c4b0ef5c7f1396a15509eac5bd46de5bf8e7
};

const ACTIVITY_REFRESH_ENABLED = 'retail.activity_refresh_q1_2025_enabled';
const SELF_DIRECTED_INVESTING_ENABLED = 'retail.self_directed_investing_enabled';
const BONUS_DEFERRALS_ENABLED = 'retail.px_bonus_deferrals_q3_2025_enabled';
const REACTIFY_USER_LEVEL_PERFORMANCE = 'retail.user_level_performance_reactify_q3_2025_enabled';
const BEDROCK_PROTOTYPE_ENABLED = 'retail.bedrock_prototype_q4_2025_enabled';
const ADVISOR_NETWORK_ENABLED = 'retail.advisor_network_q4_2025_enabled';
const NATIONAL_PARTNER_SOLO_PLANS = 'retail.px_national_partners_solos_q4_2025_enabled';
const BAN_AGREEMENTS_ENABLED = 'retail.b4a_ax_ban_q4_2025_enabled';

const withFeatureGate = (
  element: ReactElement,
  featureGate: string,
  fallback: ReactNode = <NotFound />
): ReactElement => (
  <TestTrackFeatureGate name={featureGate} context="Routes">
    <TestTrackFeatureGate.True>{element}</TestTrackFeatureGate.True>
    <TestTrackFeatureGate.False>{fallback}</TestTrackFeatureGate.False>
  </TestTrackFeatureGate>
);

export const pangeaRoutes: PangeaRouteObject[] = [
  {
    path: '/',
    element: <Home />,
    feature: 'app_home',
  },
  {
    path: `${urlBuilder.getAppChecking.path}/*`,
    element: <CheckingApp />,
    feature: 'checking_home',
  },
  {
    path: urlBuilder.appPersonalReferralInvites.path,
    element: <ReferAFriendPage />,
    feature: 'content_referrals',
    loader: loadInitialProps,
    hydrateFallbackElement: <FullPageLoader />,
  },
  {
    path: urlBuilder.appExistingUserEnrollment.path,
    element: withFeatureGate(<AgreementsApp />, BAN_AGREEMENTS_ENABLED),
    feature: 'b4a_agreements',
  },
  {
    path: `${urlBuilder.appFiveTwoNineOhioCDPurchase.path}/*`,
    element: <FiveTwoNineOhioCDPurchaseApp />,
    feature: 'investing_five_two_nine',
  },
  {
    path: `${urlBuilder.appExternallyAdvisedPlan.path}/*`,
    element: <ExternallyAdvisedPlanFlow />,
    feature: 'investing_edit_goal',
  },
  {
    path: urlBuilder.appIraDetails.path,
    element: <IraDetailsApp />,
    feature: 'investing_goals_retirement',
  },
  {
    path: urlBuilder.appFourOhOneKDetails.path,
    element: <FourOhOneKDetailsApp />,
    feature: 'investing_four_oh_one_k',
  },
  {
    path: urlBuilder.appFourOhOneKSelect.path,
    element: <FourOhOneKSelectApp />,
    feature: 'investing_four_oh_one_k',
  },
  {
    path: urlBuilder.appPersonalInfoEdit.path,
    element: <EditAddressPage />,
    feature: 'user_settings_profile_management',
    loader: loadInitialProps,
    hydrateFallbackElement: <FullPageLoader />,
  },
  {
    path: urlBuilder.appPurpose.path,
    element: <PurposePage />,
    feature: 'envelope_purpose',
  },
  {
    path: urlBuilder.appAccount.path,
    element: <AccountPage />,
    feature: 'envelope_account',
  },
  {
    path: `${urlBuilder.appPerformance.path}`,
    element: withFeatureGate(
      <UserPerformancePage />,
      REACTIFY_USER_LEVEL_PERFORMANCE,
      <PerformanceRealtimeLoaderApp />
    ),
    feature: 'performance',
  },
  {
    path: `${urlBuilder.appAccountPerformance.path}`,
    element: withFeatureGate(<AccountPerformancePage />, REACTIFY_USER_LEVEL_PERFORMANCE),
    feature: 'performance',
  },
  {
    path: urlBuilder.appActivity.path,
    element: withFeatureGate(<ActivityPage />, ACTIVITY_REFRESH_ENABLED),
    feature: 'investing_activity',
  },
  {
    path: urlBuilder.appAccountForecaster.path,
    element: <ForecasterPage />,
    feature: 'investing_forecaster',
  },
  {
    path: urlBuilder.appPurposeForecaster.path,
    element: <ForecasterPage />,
    feature: 'investing_forecaster',
  },
  {
    path: urlBuilder.appAccountSecurity.path,
    element: withFeatureGate(<AccountSecurityPage />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'account_security',
  },
  {
    path: urlBuilder.emailVerificationCheckpoint.path,
    element: <EmailVerificationCheckpointApp />,
    feature: 'user_settings_profile_contact_management',
  },
  {
    path: `${urlBuilder.appAccountBuyOrder.path}/*`,
    element: withFeatureGate(<BuyOrderFlow />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'direct_trading',
  },
  {
    path: `${urlBuilder.appAccountSellOrder.path}/*`,
    element: withFeatureGate(<SellOrderFlow />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'direct_trading',
  },
  {
    path: `${urlBuilder.appAddAccount.path}/*`,
    element: <AddAccount />,
    feature: 'add_account',
  },
  {
    path: `${urlBuilder.appAdvisorNetwork.path}/*`,
    element: withFeatureGate(<AdvisorNetworkApp />, ADVISOR_NETWORK_ENABLED),
    feature: 'advisor_network',
  },
  {
    path: `${urlBuilder.appDeferralSettings.path}/*`,
    element: withFeatureGate(<DeferralSettingsApp />, BONUS_DEFERRALS_ENABLED),
    feature: 'investing_four_oh_one_k',
  },
  {
    path: `${urlBuilder.appAgentsPlayground.path}`,
    element: withFeatureGate(<AgentsPlaygroundPage />, BEDROCK_PROTOTYPE_ENABLED),
    feature: 'agents_playground',
  },
  {
    path: '/generic_error',
    element: <GenericErrorApp />,
    feature: 'app_error_pages',
  },
  {
    path: `${urlBuilder.appEditUserLevelSettings.path}/*`,
    element: withFeatureGate(<EditUserLevelSettings />, SELF_DIRECTED_INVESTING_ENABLED),
    feature: 'edit_user_level_settings',
  },
  {
    path: '/accept_solo_401k/*',
    element: withFeatureGate(<ConfirmAdvisedSolo401kInvitation />, NATIONAL_PARTNER_SOLO_PLANS),
    feature: 'advised_401k',
  },
  ...echoRoutes.map(
    (route): PangeaRouteObject => ({
      path: route.path.replace('/:firm_key?/app/', '/'),
      feature: route.feature,
      loader: echoLoader,
      element: <EchoRoute key={route.path} />,
      errorElement: <ErrorElement owner="web_platform" feature={route.feature} />,
      hydrateFallbackElement: <FullPageLoader />,
    })
  ),
];
